import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FaBuilding, FaRocket, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  position: relative;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  text-align: center;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/img/building1.jpg') center/cover no-repeat;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: ${props => props.theme.spacing.lg};
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.95;
  max-width: 800px;
  margin: 0 auto;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.7);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const TabContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
`;

const TabNavigation = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xxl};
  border-bottom: 2px solid ${props => props.theme.colors.primaryLight};
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  position: relative;
  margin-bottom: -2px;

  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.primaryLight};
    color: white;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: 1rem;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 400px;
`;

// Greeting Tab Components
const SectionTitle = styled.h2`
  font-size: 2.8rem;
  text-align: center;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xxl};
  font-weight: 700;
  position: relative;
  padding-bottom: ${props => props.theme.spacing.md};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const StorySection = styled.section`
  background: #fafafa;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.xl};
`;

const StoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const StoryContent = styled.div`
  padding-right: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-right: 0;
    order: 2;
  }
`;

const StoryImageBox = styled(motion.div)`
  width: 100%;
  height: 550px;
  background: url('/img/CEO1.png') center/cover no-repeat;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 400px;
    order: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const IntroTitle = styled.h3`
  font-size: 3rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xl};
  font-weight: 300;
  letter-spacing: -1px;
  line-height: 1.2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const StoryText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #4a4a4a;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: 400;
  word-break: keep-all;
  white-space: pre-line;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.05rem;
    line-height: 1.7;
  }
`;

const Signature = styled.p`
  font-size: 1.3rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  margin-top: ${props => props.theme.spacing.xl};
  font-weight: 600;
  text-align: right;
  white-space: pre;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const Divider = styled.div`
  width: 60px;
  height: 2px;
  background: ${props => props.theme.colors.primary};
  margin: ${props => props.theme.spacing.xl} 0;
`;

const CompanyInfoSection = styled.section`
  background: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.xl};
`;

const CompanyInfoContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const InfoItem = styled.div`
  display: flex;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid #e0e0e0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.xs};
  }
`;

const InfoLabel = styled.div`
  min-width: 140px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
`;

const InfoValue = styled.span`
  color: #666;
  font-size: 1rem;
  flex: 1;
`;

// History Tab Components
const Timeline = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${props => props.theme.colors.primary};
    transform: translateX(-50%);

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)<{ isRight?: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    margin-left: 60px;
  }

  ${props => props.isRight ? `
    flex-direction: row-reverse;
    text-align: right;
  ` : ''}
`;

const TimelineYear = styled.div<{ isRight?: boolean }>`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: bold;
  font-size: 1.1rem;
  min-width: 100px;
  text-align: center;
  z-index: 2;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const TimelineContent = styled.div<{ isRight?: boolean }>`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  flex: 1;
  margin: ${props => props.isRight ? `0 ${props.theme.spacing.xl} 0 0` : `0 0 0 ${props.theme.spacing.xl}`};
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin: 0;
    width: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    ${props => props.isRight ? 'right' : 'left'}: -10px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border: 10px solid transparent;
    ${props => props.isRight ?
      'border-right-color: white;' :
      'border-left-color: white;'
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      display: none;
    }
  }
`;

const EventTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const EventDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
`;

// Location Tab Components
const LocationSection = styled.section`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
`;

const LocationGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xxl};
  margin-top: ${props => props.theme.spacing.xl};
`;

const LocationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: start;
  margin-bottom: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const LocationInfo = styled.div``;

const LocationItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.lg};
  gap: ${props => props.theme.spacing.md};
`;

const LocationIcon = styled.div`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-top: 4px;
`;

const LocationContent = styled.div`
  flex: 1;
`;

const LocationLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const LocationText = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab') as 'greeting' | 'overview' | 'history' | 'location' | null;
  const [activeTab, setActiveTab] = useState<'greeting' | 'overview' | 'history' | 'location'>(tabParam || 'overview');

  // URL 파라미터가 변경되면 탭 업데이트
  useEffect(() => {
    if (tabParam && ['greeting', 'overview', 'history', 'location'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Type cast icons
  const BuildingIcon = FaBuilding as IconType;
  const RocketIcon = FaRocket as IconType;
  const MapMarkerIcon = FaMapMarkerAlt as IconType;
  const PhoneIcon = FaPhone as IconType;

  const timelineData = [
    {
      year: '2022',
      title: t('about.history.timeline.2022.title'),
      description: t('about.history.timeline.2022.description'),
      icon: <BuildingIcon />
    },
    {
      year: '2017',
      title: t('about.history.timeline.2017.title'),
      description: t('about.history.timeline.2017.description'),
      icon: <BuildingIcon />
    },
    {
      year: '2011',
      title: t('about.history.timeline.2011.title'),
      description: t('about.history.timeline.2011.description'),
      icon: <RocketIcon />
    },
    {
      year: '2006',
      title: t('about.history.timeline.2006.title'),
      description: t('about.history.timeline.2006.description'),
      icon: <BuildingIcon />
    },
    {
      year: '2003',
      title: t('about.history.timeline.2003.title'),
      description: t('about.history.timeline.2003.description'),
      icon: <BuildingIcon />
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'greeting':
        return (
          <TabContent
            key="greeting"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <StorySection>
              <StoryContainer>
                <StoryContent>
                  <IntroTitle>{t('about.greeting.title')}</IntroTitle>
                  <Divider />
                  <StoryText>
                    {t('about.greeting.message1')}
                  </StoryText>
                  <StoryText>
                    {t('about.greeting.message2')}
                  </StoryText>
                  <StoryText>
                    {t('about.greeting.message3')}
                  </StoryText>
                  <StoryText>
                    {t('about.greeting.message4')}
                  </StoryText>
                  <Signature>
                    {t('about.greeting.signature')}
                  </Signature>
                </StoryContent>

                <StoryImageBox
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                />
              </StoryContainer>
            </StorySection>
          </TabContent>
        );

      case 'overview':
        return (
          <TabContent
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <StorySection>
              <StoryContainer>
                <StoryContent>
                  <IntroTitle>{t('about.overview.title')}</IntroTitle>
                  <Divider />
                  <StoryText>
                    {t('about.overview.description1')}
                  </StoryText>
                  <StoryText>
                    {t('about.overview.description2')}
                  </StoryText>
                  <StoryText>
                    {t('about.overview.description3')}
                  </StoryText>
                </StoryContent>

                <StoryImageBox
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  style={{ background: 'url(\'/img/building1.jpg\') center/cover no-repeat' }}
                />
              </StoryContainer>
            </StorySection>

            <CompanyInfoSection>
              <CompanyInfoContainer>
                <SectionTitle>{t('about.company.title')}</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>{t('about.company.name')}</InfoLabel>
                    <InfoValue>{t('about.company.nameValue')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.phone')}</InfoLabel>
                    <InfoValue>{t('about.company.phoneValue')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.ceo')}</InfoLabel>
                    <InfoValue>{t('about.company.ceoName')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.fax')}</InfoLabel>
                    <InfoValue>{t('about.company.faxValue')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.founded')}</InfoLabel>
                    <InfoValue>{t('about.company.foundedDate')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.email')}</InfoLabel>
                    <InfoValue>{t('about.company.emailValue')}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>{t('about.company.address')}</InfoLabel>
                    <InfoValue style={{ fontSize: '0.9rem' }}>{t('about.company.addressValue')}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </CompanyInfoContainer>
            </CompanyInfoSection>
          </TabContent>
        );

      case 'history':
        return (
          <TabContent
            key="history"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Timeline>
              {timelineData.map((item, index) => (
                <TimelineItem
                  key={item.year}
                  isRight={index % 2 === 1}
                  initial={{ opacity: 0, x: index % 2 === 1 ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <TimelineYear isRight={index % 2 === 1}>
                    {item.year}
                  </TimelineYear>

                  <TimelineContent isRight={index % 2 === 1}>
                    <EventTitle>{item.title}</EventTitle>
                    <EventDescription>{item.description}</EventDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </TabContent>
        );

      case 'location':
        return (
          <TabContent
            key="location"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <LocationSection>
              <SectionTitle>{t('about.location.title')}</SectionTitle>
              <LocationGrid>
                {/* 본사 및 공장 */}
                <div>
                  <SectionTitle style={{ marginBottom: '30px', fontSize: '1.8rem' }}>
                    {t('about.location.headquarters.title')}
                  </SectionTitle>
                  <LocationRow>
                    <LocationInfo>
                      <LocationItem>
                        <LocationIcon>
                          <MapMarkerIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>{t('about.location.address')}</LocationLabel>
                          <LocationText>
                            {t('about.location.headquarters.address')}<br />
                            {t('about.location.zipcode')}: {t('about.location.headquarters.zipcode')}
                          </LocationText>
                        </LocationContent>
                      </LocationItem>

                      <LocationItem>
                        <LocationIcon>
                          <PhoneIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>TEL</LocationLabel>
                          <LocationText>{t('about.location.headquarters.tel')}</LocationText>
                        </LocationContent>
                      </LocationItem>

                      <LocationItem>
                        <LocationIcon>
                          <PhoneIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>FAX</LocationLabel>
                          <LocationText>{t('about.location.headquarters.fax')}</LocationText>
                        </LocationContent>
                      </LocationItem>
                    </LocationInfo>

                    <MapContainer>
                      <iframe
                        src="https://maps.google.com/maps?q=경기도+용인시+처인구+남사읍+원암로+275&output=embed"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="본사 및 공장 위치"
                      />
                    </MapContainer>
                  </LocationRow>
                </div>

                {/* 동탄사무실 */}
                <div>
                  <SectionTitle style={{ marginBottom: '30px', fontSize: '1.8rem' }}>
                    {t('about.location.office.title')}
                  </SectionTitle>
                  <LocationRow>
                    <LocationInfo>
                      <LocationItem>
                        <LocationIcon>
                          <MapMarkerIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>{t('about.location.address')}</LocationLabel>
                          <LocationText>
                            {t('about.location.office.address')}<br />
                            {t('about.location.zipcode')}: {t('about.location.office.zipcode')}
                          </LocationText>
                        </LocationContent>
                      </LocationItem>

                      <LocationItem>
                        <LocationIcon>
                          <PhoneIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>TEL</LocationLabel>
                          <LocationText>{t('about.location.office.tel')}</LocationText>
                        </LocationContent>
                      </LocationItem>

                      <LocationItem>
                        <LocationIcon>
                          <PhoneIcon />
                        </LocationIcon>
                        <LocationContent>
                          <LocationLabel>FAX</LocationLabel>
                          <LocationText>{t('about.location.office.fax')}</LocationText>
                        </LocationContent>
                      </LocationItem>
                    </LocationInfo>

                    <MapContainer>
                      <iframe
                        src="https://maps.google.com/maps?q=경기도+화성시+동탄감배산로+143+동탄역+유림노르웨이숲+202동+2210호&output=embed"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="동탄사무실 위치"
                      />
                    </MapContainer>
                  </LocationRow>
                </div>
              </LocationGrid>
            </LocationSection>
          </TabContent>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t('about.title')}
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('about.subtitle')}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <TabContainer>
        <TabNavigation>
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          >
            {t('about.overview.title')}
          </TabButton>
          <TabButton
            active={activeTab === 'greeting'}
            onClick={() => setActiveTab('greeting')}
          >
            {t('about.greeting.title')}
          </TabButton>
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
          >
            {t('about.history.title')}
          </TabButton>
          <TabButton
            active={activeTab === 'location'}
            onClick={() => setActiveTab('location')}
          >
            {t('about.location.title')}
          </TabButton>
        </TabNavigation>

        <AnimatePresence mode="wait">
          {renderTabContent()}
        </AnimatePresence>
      </TabContainer>
    </Container>
  );
};

export default AboutPage;
